import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import React, { Suspense, lazy } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AuthCallback } from './components/auth/AuthCallback';
import { DashboardGate } from './components/auth/DashboardGate';
import { AppShell } from './components/layout/AppShell';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { Forbidden } from './pages/Forbidden';
import { NotFound } from './pages/NotFound';
import { InstallBanner } from './components/shared/InstallBanner';
import { OfflineIndicator } from './components/shared/OfflineIndicator';
import { Toaster } from './components/ui/sonner';
import './index.css';

// Lazy-loaded pages for performance
const Dashboard = lazy(() => import('./pages/client/Dashboard').then(m => ({ default: m.Dashboard })));
const ProductsPage = lazy(() => import('./pages/client/ProductsPage').then(m => ({ default: m.ProductsPage })));
const QuoteFormPage = lazy(() => import('./pages/client/QuoteFormPage').then(m => ({ default: m.QuoteFormPage })));
const RCVQuoteWizardPage = lazy(() => import('./pages/client/RCVQuoteWizardPage'));
const QuotesPage = lazy(() => import('./pages/client/QuotesPage').then(m => ({ default: m.QuotesPage })));
const QuoteDetailPage = lazy(() => import('./pages/client/QuoteDetailPage').then(m => ({ default: m.QuoteDetailPage })));
const PoliciesPage = lazy(() => import('./pages/client/PoliciesPage').then(m => ({ default: m.PoliciesPage })));
const PolicyDetailPage = lazy(() => import('./pages/client/PolicyDetailPage').then(m => ({ default: m.PolicyDetailPage })));
const DocumentsPage = lazy(() => import('./pages/client/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const RequestsPage = lazy(() => import('./pages/client/RequestsPage').then(m => ({ default: m.RequestsPage })));
const RequestCreatePage = lazy(() => import('./pages/client/RequestCreatePage').then(m => ({ default: m.RequestCreatePage })));
const RequestDetailPage = lazy(() => import('./pages/client/RequestDetailPage').then(m => ({ default: m.RequestDetailPage })));
const ProfilePage = lazy(() => import('./pages/client/ProfilePage').then(m => ({ default: m.ProfilePage })));
const NotificationsPage = lazy(() => import('./pages/client/NotificationsPage'));
const PaymentsHistoryPage = lazy(() => import('./pages/client/PaymentsHistoryPage').then(m => ({ default: m.PaymentsHistoryPage })));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminUserDetailPage = lazy(() => import('./pages/admin/AdminUserDetailPage').then(m => ({ default: m.AdminUserDetailPage })));
const AdminRequestsPage = lazy(() => import('./pages/admin/AdminRequestsPage').then(m => ({ default: m.AdminRequestsPage })));
const AdminRequestDetailPage = lazy(() => import('./pages/admin/AdminRequestDetailPage').then(m => ({ default: m.AdminRequestDetailPage })));
const AdminDocumentsPage = lazy(() => import('./pages/admin/AdminDocumentsPage').then(m => ({ default: m.AdminDocumentsPage })));
const AdminQuotesPage = lazy(() => import('./pages/admin/AdminQuotesPage').then(m => ({ default: m.AdminQuotesPage })));
const AdminPoliciesPage = lazy(() => import('./pages/admin/AdminPoliciesPage').then(m => ({ default: m.AdminPoliciesPage })));
const AdminAcselAuditPage = lazy(() => import('./pages/admin/AdminAcselAuditPage').then(m => ({ default: m.AdminAcselAuditPage })));
const SuperAdminDashboard = lazy(() => import('./pages/superadmin/SuperAdminDashboard'));
const UsersReport = lazy(() => import('./pages/superadmin/UsersReport'));
const QuotesReport = lazy(() => import('./pages/superadmin/QuotesReport'));
const PoliciesReport = lazy(() => import('./pages/superadmin/PoliciesReport'));
const RequestsReport = lazy(() => import('./pages/superadmin/RequestsReport'));
const ProductsManagement = lazy(() => import('./pages/superadmin/ProductsManagement'));
const AuditTrailPage = lazy(() => import('./pages/superadmin/AuditTrailPage'));
const TelemarketingLogin = lazy(() => import('./pages/telemarketing/TelemarketingLogin').then(m => ({ default: m.TelemarketingLogin })));
const TelemarketingDashboard = lazy(() => import('./pages/telemarketing/TelemarketingDashboard').then(m => ({ default: m.TelemarketingDashboard })));
const TelemarketingReports = lazy(() => import('./pages/telemarketing/TelemarketingReports').then(m => ({ default: m.TelemarketingReports })));
const PaymentSuccess = lazy(() => import('./pages/telemarketing/PaymentResult').then(m => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import('./pages/telemarketing/PaymentResult').then(m => ({ default: m.PaymentCancel })));
const PayCheckout = lazy(() => import('./pages/pay/PayCheckout').then(m => ({ default: m.PayCheckout })));

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <img src="/isologo-azul.png" alt="" className="w-10 h-10 object-contain animate-pulse" />
  </div>
);

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/callback/google" element={<AuthCallback />} />
      <Route path="/auth/callback/apple" element={<AuthCallback />} />
      <Route path="/auth/callback/meta" element={<AuthCallback />} />
      <Route path="/forbidden" element={<Forbidden />} />

      {/* Whitelabel public checkout */}
      <Route path="/pagar/:orderRef" element={<Suspense fallback={<PageLoader />}><PayCheckout /></Suspense>} />

      {/* Telemarketing (separate access route) */}
      <Route path="/telemarketing/login" element={<Suspense fallback={<PageLoader />}><TelemarketingLogin /></Suspense>} />
      <Route path="/telemarketing/pago-exitoso" element={<Suspense fallback={<PageLoader />}><PaymentSuccess /></Suspense>} />
      <Route path="/telemarketing/pago-cancelado" element={<Suspense fallback={<PageLoader />}><PaymentCancel /></Suspense>} />
      <Route
        path="/telemarketing"
        element={
          <ProtectedRoute requiredPermission="can_view_telemarketing">
            <Suspense fallback={<PageLoader />}>
              <TelemarketingDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/telemarketing/dashboard"
        element={
          <ProtectedRoute requiredPermission="can_view_telemarketing">
            <Suspense fallback={<PageLoader />}>
              <TelemarketingDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/telemarketing/reportes"
        element={
          <ProtectedRoute requiredPermission="can_view_telemarketing">
            <Suspense fallback={<PageLoader />}>
              <TelemarketingReports />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Protected Routes with AppShell */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardGate />} />
        
        {/* Products & Quotes */}
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/rcv/quote" element={<RCVQuoteWizardPage />} />
        <Route path="quote/rcv" element={<RCVQuoteWizardPage />} />
        <Route path="products/:productId/quote" element={<QuoteFormPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="quotes/:quoteId" element={<QuoteDetailPage />} />
        
        {/* Policies */}
        <Route path="policies" element={<PoliciesPage />} />
        <Route path="policies/:policyId" element={<PolicyDetailPage />} />
        <Route path="payments" element={<PaymentsHistoryPage />} />
        
        {/* Documents */}
        <Route path="documents" element={<DocumentsPage />} />
        
        {/* Requests */}
        <Route path="requests" element={<RequestsPage />} />
        <Route path="requests/new" element={<RequestCreatePage />} />
        <Route path="requests/:requestId" element={<RequestDetailPage />} />
        
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />
        
        {/* Notifications */}
        <Route path="notifications" element={<NotificationsPage />} />
        
        {/* Admin Routes */}
        <Route
          path="admin"
          element={
            <ProtectedRoute requiredPermission="can_view_admin">
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute requiredPermission="can_manage_users">
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/users/:userId"
          element={
            <ProtectedRoute requiredPermission="can_manage_users">
              <AdminUserDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/requests"
          element={
            <ProtectedRoute requiredPermission="can_manage_requests">
              <AdminRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/requests/:requestId"
          element={
            <ProtectedRoute requiredPermission="can_manage_requests">
              <AdminRequestDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/documents"
          element={
            <ProtectedRoute requiredPermission="can_validate_documents">
              <AdminDocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/quotes"
          element={
            <ProtectedRoute requiredPermission="can_view_admin">
              <AdminQuotesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/policies"
          element={
            <ProtectedRoute requiredPermission="can_view_all_policies">
              <AdminPoliciesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/acsel-audit"
          element={
            <ProtectedRoute requiredPermission="can_view_admin">
              <AdminAcselAuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/audit"
          element={
            <ProtectedRoute requiredPermission="can_view_admin">
              <AuditTrailPage />
            </ProtectedRoute>
          }
        />
        
        {/* SuperAdmin Routes */}
        <Route
          path="superadmin"
          element={
            <ProtectedRoute requiredPermission="can_view_superadmin">
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="superadmin/report/users"
          element={
            <ProtectedRoute requiredPermission="can_view_superadmin">
              <UsersReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="superadmin/report/quotes"
          element={
            <ProtectedRoute requiredPermission="can_view_superadmin">
              <QuotesReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="superadmin/report/policies"
          element={
            <ProtectedRoute requiredPermission="can_view_superadmin">
              <PoliciesReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="superadmin/report/requests"
          element={
            <ProtectedRoute requiredPermission="can_view_superadmin">
              <RequestsReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="superadmin/products"
          element={
            <ProtectedRoute requiredPermission="can_view_superadmin">
              <ProductsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="superadmin/audit"
          element={
            <ProtectedRoute requiredPermission="can_view_superadmin">
              <AuditTrailPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  // Register Service Worker on app load
  React.useEffect(() => {
    import('./lib/pwa').then(({ registerServiceWorker }) => {
      registerServiceWorker();
    });
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <OfflineIndicator />
        <AppRouter />
        <Toaster position="top-right" richColors />
        <InstallBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
